// ganttUtils.js - Enhanced utility functions with recurring task support using RRule
import { RRule } from 'rrule';
import { format } from 'date-fns';
import i18n from '../../../i18n';
import { getDateLocale } from '../../../i18n/LocalizedDateDistance';
import { capitalizeFirst } from "../../../utils/formatters/capitalize";
import { formatDateToString } from "../../../utils/formatters/dateFormatter";
import { TASK_HEIGHT, TASK_SPACING, CATEGORY_PADDING } from '../constants/ganttConstants';
import { daysDifference } from '../../../i18n/dateUtils';

// Re-export constants for backward compatibility
export { TASK_HEIGHT, TASK_SPACING, CATEGORY_PADDING };

export const getDateIndex = (date, baseDate) => {
  // Use normalized date difference to avoid timezone issues
  return daysDifference(date, baseDate);
};

// Generate ghost tasks for recurring tasks within a date range using RRule
export const generateGhostTasks = (task, viewStartDate, viewEndDate, maxInstances = 50, closingDaysMap = null) => {
  if (!task.is_recurring || !task.rrule || task.state === "COMPLETED" || task.state === "CANCELLED") {
    return [];
  }

  try {
    // Parse RRULE - it may include DTSTART or be RRULE-only
    let rule;
    if (task.rrule.includes('DTSTART')) {
      // DTSTART is in the string, parse as-is
      rule = RRule.fromString(task.rrule);
    } else {
      // RRULE only - need to provide dtstart from task's start date
      const dtstart = new Date(task.startDate);
      // Normalize to UTC midnight to match server behavior
      const utcStart = Date.UTC(
        dtstart.getFullYear(),
        dtstart.getMonth(),
        dtstart.getDate(),
        0, 0, 0, 0
      );
      rule = RRule.fromString(task.rrule, { dtstart: new Date(utcStart) });
    }

    const ghostTasks = [];
    const taskDuration = task.endDate - task.startDate; // Duration in milliseconds

    // Normalize original task's start date to UTC midnight
    const originalStartDate = new Date(task.startDate);
    const utcOriginalStart = Date.UTC(
      originalStartDate.getFullYear(),
      originalStartDate.getMonth(),
      originalStartDate.getDate(),
      0, 0, 0, 0
    );

    // Get all occurrences after the original task
    const occurrences = rule.between(
      new Date(utcOriginalStart + 86400000), // Start from next day (exclude original)
      viewEndDate,
      true // inclusive
    );

    // Limit to maxInstances
    const limitedOccurrences = occurrences.slice(0, maxInstances);

    limitedOccurrences.forEach((occurrence, index) => {
      // Normalize occurrence to local midnight to match original task time component
      const occurrenceUTC = new Date(occurrence);
      const currentStartDate = new Date(
        occurrenceUTC.getUTCFullYear(),
        occurrenceUTC.getUTCMonth(),
        occurrenceUTC.getUTCDate(),
        originalStartDate.getHours(),
        originalStartDate.getMinutes(),
        originalStartDate.getSeconds()
      );

      const currentEndDate = new Date(currentStartDate.getTime() + taskDuration);

      // Filter ghost tasks on closing days (unless skip_closing_days is true)
      if (closingDaysMap && !task.skip_closing_days) {
        const dateStr = formatDateToString(currentStartDate);
        if (closingDaysMap.has(dateStr)) {
          return; // Skip this ghost task - it falls on a closing day
        }
      }

      // Only include if it overlaps with our view range
      if (currentEndDate >= viewStartDate && currentStartDate <= viewEndDate) {
        const ghostTask = {
          ...task,
          id: `${task.id}-ghost-${index}`,
          startDate: currentStartDate,
          endDate: currentEndDate,
          isGhost: true,
          originalTaskId: task.id,
          instanceNumber: index + 1,
          name: `${task.name} (${index + 1})`
        };

        ghostTasks.push(ghostTask);
      }
    });

    return ghostTasks;
  } catch (error) {
    console.error('Failed to generate ghost tasks from rrule:', error);
    return [];
  }
};

// Enhanced function to get all tasks including ghost tasks
export const getAllTasksWithGhosts = (categories, viewStartDate, viewEndDate, maxInstances = 50, closingDaysMap = null) => {
  return categories.map(category => {
    const allTasks = [...category.tasks];

    // For each task in the category, generate ghost tasks if it's recurring
    category.tasks.forEach(task => {
      const ghostTasks = generateGhostTasks(task, viewStartDate, viewEndDate, maxInstances, closingDaysMap);
      allTasks.push(...ghostTasks);
    });

    return {
      ...category,
      tasks: allTasks
    };
  });
};

export const getCategoryHeight = (tasks, baseDate = new Date()) => {
  const positioned = calculateTaskPositions(tasks, baseDate);
  const maxLevel = positioned.length > 0 ? Math.max(...positioned.map(p => p.level)) : 0;
  const numLevels = Math.max(maxLevel + 1, 1);
  return CATEGORY_PADDING * 2 + numLevels * (TASK_HEIGHT + TASK_SPACING) - TASK_SPACING;
};

export const calculateTaskPositions = (tasks, baseDate) => {
  if (!tasks || tasks.length === 0) {
    return [];
  }

  const sortedTasks = [...tasks].sort((a, b) => {
    const dateDiff = getDateIndex(a.startDate, baseDate) - getDateIndex(b.startDate, baseDate);
    if (dateDiff !== 0) return dateDiff;
    return (a.name || '').localeCompare(b.name || '');
  });
  const positioned = [];

  sortedTasks.forEach(task => {
    const taskStart = getDateIndex(task.startDate, baseDate);
    const taskEnd = getDateIndex(task.endDate, baseDate);

    // Find the first available row (level) for this task
    let level = 0;
    let conflictFound = true;

    while (conflictFound) {
      conflictFound = false;

      // Check if this level conflicts with any existing task at this level
      for (const positionedTask of positioned) {
        if (positionedTask.level === level) {
          const existingStart = getDateIndex(positionedTask.task.startDate, baseDate);
          const existingEnd = getDateIndex(positionedTask.task.endDate, baseDate);

          // Check for overlap
          if (!(taskEnd < existingStart || taskStart > existingEnd)) {
            conflictFound = true;
            break;
          }
        }
      }

      if (conflictFound) {
        level++;
      }
    }

    positioned.push({ task, level });
  });

  return positioned;
};

export const formatMonth = (date) => {
  return capitalizeFirst(format(date, 'LLLL', { locale: getDateLocale(i18n.language) }));
};

export const formatLocalizedDate = (date, formatString) => {
  return format(date, formatString, { locale: getDateLocale(i18n.language) });
};

export const formatDay = (date) => {
  return date.getDate();
};

// Utility to get recurring task description from RRule
export const getRecurringDescription = (task) => {
  if (!task.is_recurring || !task.rrule) return null;

  try {
    const rule = RRule.fromString(task.rrule);
    return rule.toText(); // Returns human-readable text like "every week on Monday"
  } catch (error) {
    console.error('Failed to parse rrule:', error);
    return 'Recurring task';
  }
};